// =====================================================================
// PLENARIUM - Eleicao da Mesa Diretora no PainelTV (PLENARIUM:ELEICAO_MESA)
// Ocupa a tela da TV (por cima) enquanto a eleicao acontece:
//  - EM VOTACAO: foto de cada vereador. Secreta: "VOTOU" (sem dizer em
//    quem) + "08 / 11". Aberta: a chapa de cada um + parcial.
//  - EMPATE: faixa "aguardando definicao da Mesa" ate o admin decidir;
//  - CONCLUIDA: chapa eleita com a foto de cada membro, por 3 minutos.
// Fora disso nao aparece (a TV segue normal). Conversa so com
// /api/eleicoes e escuta 'eleicao-atualizada'; reconecta sozinho.
// v2: fotos dos vereadores na votacao e dos eleitos no resultado.
// v3: foto inteira (card no formato 3x4, medido na tela) e a chapa votada
// v4: fileiras equilibradas (11 vereadores = 6 + 5).
//     em faixa colorida grande; nome da chapa sem repetir "Chapa 1 · Chapa 1".
// =====================================================================
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';

const CORES = ['#3b82f6', '#14b8a6', '#f97316', '#a855f7', '#ec4899', '#84cc16'];
const nomeV = (v) => (v ? (v.nomeParlamentar || v.nome) : '');
const pad = (n) => String(n ?? 0).padStart(2, '0');
const iniciais = (nome) => String(nome || '?').split(' ').filter(Boolean).map(p => p[0]).slice(0, 2).join('').toUpperCase();

function Hora() {
  const [agora, setAgora] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setAgora(new Date()), 1000); return () => clearInterval(t); }, []);
  return (
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontFamily: 'monospace', fontSize: '3.4vh', fontWeight: 700, letterSpacing: 3 }}>{agora.toLocaleTimeString('pt-BR')}</div>
      <div style={{ fontSize: '1.2vh', color: '#94a3b8' }}>{agora.toLocaleDateString('pt-BR')}</div>
    </div>
  );
}

function Foto({ url, nome, apagada, inteira }) {
  return (
    <div style={{ flex: 1, minHeight: 0, background: 'rgba(255,255,255,.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
      {url
        ? <img src={url} alt={nome} style={{ width: '100%', height: '100%', objectFit: inteira ? 'contain' : 'cover', objectPosition: 'top', filter: apagada ? 'grayscale(.7)' : 'none' }} />
        : <span style={{ fontSize: '5vh', fontWeight: 900, color: 'rgba(255,255,255,.3)' }}>{iniciais(nome)}</span>}
    </div>
  );
}

// "Chapa 1 · Uniao"; se o nome for so "Chapa 1", fica "Chapa 1".
const rotuloChapa = (c) => { const limpo = String(c.nome || '').replace(/^chapa\s+/i, '').trim(); return (!limpo || limpo === String(c.numero)) ? `Chapa ${c.numero}` : `Chapa ${c.numero} · ${limpo}`; };

// Melhor arrumacao dos cards 3x4 no espaco medido: testa 1..n colunas e
// fica com a que deixa a foto maior, sem cortar.
function arrumar(n, W, H) {
  const vh = (typeof window !== 'undefined' ? window.innerHeight : 1080) / 100;
  const gap = 1.4 * vh, rotulo = 8.6 * vh;
  let melhor = { cols: Math.max(n, 1), w: 0 };
  for (let cols = 1; cols <= Math.max(n, 1); cols++) {
    const rows = Math.ceil(n / cols);
    const porLargura = (W - (cols - 1) * gap) / cols - 2;
    const porAltura = ((H - (rows - 1) * gap) / rows - rotulo - 2) * 0.75 + 1.1 * vh;
    const w = Math.floor(Math.min(porLargura, porAltura));
    if (w > melhor.w) melhor = { cols, w };
  }
  // Divide igual entre as fileiras (11 -> 6+5, nao 9+2). Com menos
  // colunas e o mesmo numero de fileiras, a foto nunca fica menor.
  const rows = Math.ceil(n / melhor.cols) || 1;
  const cols = Math.ceil(n / rows) || 1;
  return { cols, w: melhor.w, gap };
}

export default function EleicaoMesaTV({ sessaoId, camaraNome, sessaoTitulo, vereadores = [] }) {
  const [lista, setLista] = useState([]);
  const [resultado, setResultado] = useState(null);
  const vistoRef = useRef({});
  const [tam, setTam] = useState({ w: 0, h: 0 });
  const obsRef = useRef(null);
  // Mede o espaco da grade de fotos (e acompanha se a janela mudar).
  const refGrade = useCallback((no) => {
    if (obsRef.current) { obsRef.current.disconnect(); obsRef.current = null; }
    if (no && typeof ResizeObserver !== 'undefined') {
      const o = new ResizeObserver(([e]) => setTam({ w: e.contentRect.width, h: e.contentRect.height }));
      o.observe(no); obsRef.current = o;
    }
  }, []);

  const carregar = useCallback(async () => {
    if (!sessaoId) { setLista([]); return; }
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`/api/eleicoes/sessao/${sessaoId}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      const dados = Array.isArray(r.data) ? r.data : [];
      dados.forEach(el => {
        const antes = vistoRef.current[el.id];
        if (antes && antes !== 'CONCLUIDA' && el.status === 'CONCLUIDA') setResultado(el.id);
        vistoRef.current[el.id] = el.status;
      });
      setLista(dados);
    } catch { /* sem eleicao: TV normal */ }
  }, [sessaoId]);

  useEffect(() => {
    carregar();
    if (!sessaoId) return undefined;
    const socket = io(process.env.REACT_APP_API_URL || 'http://localhost:3001');
    let primeira = true;
    socket.on('connect', () => {
      socket.emit('entrar-sala', `sessao-${sessaoId}`);
      if (!primeira) carregar();
      primeira = false;
    });
    socket.on('eleicao-atualizada', (d) => { if (!d || d.sessaoId === sessaoId) carregar(); });
    // Rede de seguranca: se um aviso se perder, confere a cada 20 s.
    const t = setInterval(carregar, 20000);
    return () => { clearInterval(t); socket.disconnect(); };
  }, [sessaoId, carregar]);

  useEffect(() => {
    if (!resultado) return undefined;
    const t = setTimeout(() => setResultado(null), 180000);
    return () => clearTimeout(t);
  }, [resultado]);

  const atual = lista.find(e => e.status === 'EM_VOTACAO') || lista.find(e => e.status === 'EMPATE') || (resultado ? lista.find(e => e.id === resultado && e.status === 'CONCLUIDA') : null);
  if (!atual) return null;

  const porId = Object.fromEntries(vereadores.map(v => [v.id, v]));
  const fotoDe = (id) => porId[id]?.fotoUrl || '';
  const cor = (chapaId) => { const i = atual.chapas.findIndex(c => c.id === chapaId); return i < 0 ? '#ca8a04' : CORES[i % CORES.length]; };
  const votaramIds = new Set(atual.votantes.map(v => v.vereadorId));
  const pessoas = vereadores.length ? vereadores : atual.votantes.map(v => ({ id: v.vereadorId, nome: v.nome }));
  const eleita = atual.chapas.find(c => c.id === atual.chapaEleitaId);
  const arr = arrumar(pessoas.length, tam.w, tam.h);
  const cardW = Math.max(arr.w, 0);
  const borda2 = 1.1 * ((typeof window !== 'undefined' ? window.innerHeight : 1080) / 100); // as duas bordas do card

  const grade = (
    <div ref={refGrade} style={{ flex: 1, minWidth: 0, minHeight: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignContent: 'center', gap: arr.gap, maxWidth: arr.cols * cardW + (arr.cols - 1) * arr.gap + 1 }}>
      {cardW > 0 && pessoas.map(v => {
        let borda = 'rgba(255,255,255,.12)', fundo = 'rgba(255,255,255,.03)', faixa = null, apagada = true;
        if (atual.votoSecreto) {
          if (votaramIds.has(v.id)) { borda = '#22c55e'; fundo = 'rgba(34,197,94,.14)'; faixa = { txt: 'VOTOU ✓', bg: '#16a34a', fg: '#fff' }; apagada = false; }
        } else {
          const voto = atual.votosAbertos.find(x => x.vereadorId === v.id);
          if (voto) {
            const ch = atual.chapas.find(c => c.id === voto.chapaId);
            borda = cor(voto.chapaId); fundo = `${borda}26`; apagada = false;
            faixa = ch ? { txt: `CHAPA ${ch.numero}`, bg: borda, fg: '#fff' } : { txt: 'ABSTENÇÃO', bg: '#ca8a04', fg: '#1c1003' };
          }
        }
        return (
          <div key={v.id} style={{ width: cardW, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', borderRadius: '1.2vh', overflow: 'hidden', border: `0.55vh solid ${borda}`, background: fundo, opacity: apagada ? 0.5 : 1, boxShadow: apagada ? 'none' : `0 0 2.4vh ${borda}66`, transition: 'all .35s ease' }}>
            <div style={{ height: Math.round((cardW - borda2) / 0.75), display: 'flex' }}><Foto url={v.fotoUrl} nome={nomeV(v)} apagada={apagada} /></div>
            <div style={{ padding: '0.6vh 0.6vh 0', textAlign: 'center', background: 'rgba(0,0,0,.35)' }}>
              <div style={{ fontSize: '1.6vh', fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingBottom: '0.5vh' }}>{nomeV(v)}</div>
            </div>
            <div style={{ height: '3.4vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: faixa ? faixa.bg : 'rgba(0,0,0,.35)', color: faixa ? faixa.fg : '#64748b', fontSize: faixa ? '2.2vh' : '1.5vh', fontWeight: 900, letterSpacing: 1.5, whiteSpace: 'nowrap', overflow: 'hidden' }}>
              {faixa ? faixa.txt : 'AGUARDANDO'}
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'linear-gradient(160deg,#0a1628 0%,#0f2847 55%,#0a1628 100%)', color: '#fff', display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: 'inherit' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '2.4vh 2.8vh 1.6vh', borderBottom: '0.3vh solid #1e3a8a', background: 'linear-gradient(90deg,#071020,#0d1f3c)' }}>
        <div>
          <div style={{ fontSize: '2.8vh', fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase' }}>{camaraNome || 'Câmara Municipal'}</div>
          <div style={{ fontSize: '1.1vh', letterSpacing: 3, color: '#94a3b8', marginTop: 4 }}>PODER LEGISLATIVO</div>
        </div>
        <Hora />
      </div>
      <div style={{ padding: '0.8vh 2vh', background: 'rgba(0,0,0,.35)', fontSize: '1.2vh', color: '#cbd5e1', fontWeight: 700 }}>
        <span style={{ color: '#22c55e', letterSpacing: 2, marginRight: 12 }}>● SESSÃO EM ANDAMENTO</span>{sessaoTitulo || ''}
      </div>

      <div style={{ flex: 1, padding: '2.2vh 3vh', display: 'flex', flexDirection: 'column', gap: '1.8vh', minHeight: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
          <div>
            {/* PLENARIUM:ELEICAO_TV_DESTAQUE -- titulo grande */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '1.4vh', padding: '0.9vh 2.4vh 0.9vh 1.2vh', borderRadius: 999, background: 'rgba(139,92,246,.2)', border: '0.3vh solid rgba(196,181,253,.55)', boxShadow: '0 0 3vh rgba(139,92,246,.25)' }}>
              {atual.status === 'EM_VOTACAO'
                ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.8vh', background: '#dc2626', color: '#fff', fontWeight: 900, fontSize: '1.9vh', letterSpacing: '0.2vh', padding: '0.6vh 1.6vh', borderRadius: 999 }}><span style={{ width: '1.2vh', height: '1.2vh', borderRadius: '50%', background: '#fff', animation: 'emtvPulso 1.2s ease infinite' }} />AO VIVO</span>
                : <span style={{ background: '#22c55e', color: '#052e16', fontWeight: 900, fontSize: '1.9vh', letterSpacing: '0.2vh', padding: '0.6vh 1.6vh', borderRadius: 999 }}>RESULTADO</span>}
              <span style={{ fontSize: '3.2vh', fontWeight: 900, letterSpacing: '0.35vh', color: '#ede9fe' }}>ELEIÇÃO DA MESA DIRETORA</span>
            </div>
            <style>{'@keyframes emtvPulso { 0%,100% { opacity: 1; } 50% { opacity: .3; } } @keyframes emtvBrilho { 0%,100% { box-shadow: 0 0 2vh rgba(34,197,94,.55); } 50% { box-shadow: 0 0 5vh rgba(34,197,94,.95); } }'}</style>
            <div style={{ fontSize: '4.6vh', fontWeight: 900, marginTop: '1vh' }}>Biênio {atual.bienio}</div>
          </div>
          <span style={{ background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.2)', padding: '1vh 2vh', borderRadius: 999, fontWeight: 800, fontSize: '1.8vh' }}>{atual.votoSecreto ? '🔒 Voto secreto' : '🗳️ Voto aberto'}</span>
        </div>

        {/* EM VOTACAO: fotos + painel lateral */}
        {atual.status === 'EM_VOTACAO' && (
          <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: '2.2vh' }}>
            {grade}
            <div style={{ flex: '0 0 40vh', background: 'rgba(255,255,255,.03)', border: '0.2vh solid rgba(255,255,255,.08)', borderRadius: '1.6vh', padding: '2vh', display: 'flex', flexDirection: 'column', gap: '1.4vh', minHeight: 0 }}>
              {atual.votoSecreto ? (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '1.6vh', textAlign: 'center' }}>
                  <div style={{ fontSize: '2vh', color: '#cbd5e1', fontWeight: 800, letterSpacing: 2 }}>VOTAÇÃO EM ANDAMENTO</div>
                  <div style={{ fontFamily: 'monospace', fontSize: '13vh', fontWeight: 900, lineHeight: 1 }}>{pad(atual.votaram)}</div>
                  <div style={{ fontFamily: 'monospace', fontSize: '4vh', color: '#64748b', fontWeight: 800 }}>de {pad(atual.totalVereadores)}</div>
                  <div style={{ fontSize: '2vh', color: '#94a3b8' }}>vereadores já votaram</div>
                  <div style={{ fontSize: '1.5vh', color: '#64748b', marginTop: '1vh' }}>🔒 O resultado aparece ao encerrar a votação.</div>
                </div>
              ) : (
                <>
                  <div style={{ color: '#c4b5fd', fontWeight: 800, letterSpacing: 3, fontSize: '1.5vh' }}>PARCIAL</div>
                  {atual.chapas.map(c => (
                    <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '1.2vh 1.6vh', borderRadius: '1.2vh', background: `${cor(c.id)}26`, color: cor(c.id), fontWeight: 800, fontSize: '1.9vh' }}>
                      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rotuloChapa(c)}</span><b style={{ fontFamily: 'monospace', fontSize: '4.4vh', flexShrink: 0 }}>{pad(c.votos)}</b>
                    </div>
                  ))}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.2vh 1.6vh', borderRadius: '1.2vh', background: 'rgba(202,138,4,.15)', color: '#fde68a', fontWeight: 800, fontSize: '1.9vh' }}>
                    <span>Abstenção</span><b style={{ fontFamily: 'monospace', fontSize: '4.4vh' }}>{pad(atual.abstencoes)}</b>
                  </div>
                  <div style={{ marginTop: 'auto', textAlign: 'center', color: '#94a3b8', fontSize: '2vh' }}>{atual.votaram} de {atual.totalVereadores} já votaram</div>
                </>
              )}
            </div>
          </div>
        )}

        {/* RESULTADO: chapa eleita com as fotos dos membros */}
        {atual.status === 'CONCLUIDA' && eleita && (
          <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: '2.2vh' }}>
            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1.6vh', borderRadius: '1.8vh', border: '0.4vh solid #22c55e', background: 'rgba(34,197,94,.1)', boxShadow: '0 0 5vh rgba(34,197,94,.3)', padding: '2.2vh' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1.6vh', flexWrap: 'wrap' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '1vh', background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: '#fff', fontWeight: 900, letterSpacing: '0.35vh', fontSize: '3.2vh', padding: '1vh 3vh', borderRadius: 999, textShadow: '0 0.2vh 0.6vh rgba(0,0,0,.35)', animation: 'emtvBrilho 2s ease infinite' }}>✔ CHAPA ELEITA</span>
                <span style={{ fontSize: '4.2vh', fontWeight: 900 }}>{rotuloChapa(eleita)}</span>
                <span style={{ marginLeft: 'auto', fontSize: '2.8vh', color: '#bbf7d0', fontWeight: 900 }}>{eleita.votos} voto(s)</span>
              </div>
              {atual.desempatePor && <div style={{ fontSize: '1.7vh', color: '#cbd5e1' }}>Definida em caso de empate{atual.desempateFundamento ? `, conforme ${atual.desempateFundamento}` : ', conforme o Regimento Interno'}.</div>}
              <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: `repeat(${Math.max(eleita.membros.length, 1)}, 1fr)`, gap: '1.8vh' }}>
                {eleita.membros.map(m => (
                  <div key={m.cargo} style={{ minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', borderRadius: '1.4vh', overflow: 'hidden', border: '0.3vh solid rgba(34,197,94,.6)', background: 'rgba(0,0,0,.25)' }}>
                    <Foto url={fotoDe(m.vereadorId)} nome={m.nome} inteira />
                    <div style={{ padding: '1.2vh', textAlign: 'center' }}>
                      <div style={{ fontSize: '1.6vh', fontWeight: 900, color: '#86efac', letterSpacing: 1.5, textTransform: 'uppercase' }}>{m.cargo}</div>
                      <div style={{ fontSize: '2.3vh', fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.nome}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ flex: '0 0 50vh', display: 'flex', flexDirection: 'column', gap: '1.8vh', background: 'rgba(255,255,255,.03)', border: '0.2vh solid rgba(255,255,255,.08)', borderRadius: '1.6vh', padding: '2.4vh' }}>
              <div style={{ color: '#c4b5fd', fontWeight: 900, letterSpacing: '0.4vh', fontSize: '2.4vh' }}>PLACAR</div>
              {atual.chapas.map(c => {
                const ganhou = c.id === atual.chapaEleitaId;
                return (
                  <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '1.8vh 2.2vh', borderRadius: '1.6vh', background: ganhou ? 'rgba(34,197,94,.2)' : 'rgba(255,255,255,.05)', border: `0.35vh solid ${ganhou ? '#22c55e' : 'transparent'}`, color: ganhou ? '#fff' : '#cbd5e1', fontWeight: 900, fontSize: '2.8vh' }}>
                    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rotuloChapa(c)}</span><b style={{ fontFamily: 'monospace', fontSize: '8vh', lineHeight: 1, flexShrink: 0 }}>{pad(c.votos)}</b>
                  </div>
                );
              })}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.8vh 2.2vh', borderRadius: '1.6vh', background: 'rgba(202,138,4,.14)', color: '#fde68a', fontWeight: 900, fontSize: '2.8vh' }}>
                <span>Abstenções</span><b style={{ fontFamily: 'monospace', fontSize: '8vh', lineHeight: 1 }}>{pad(atual.abstencoes)}</b>
              </div>
              <div style={{ marginTop: 'auto', textAlign: 'center', color: '#cbd5e1', fontSize: '2.8vh', fontWeight: 800 }}>Votaram <b style={{ color: '#fff' }}>{atual.votaram}</b> de {atual.totalVereadores}</div>
            </div>
          </div>
        )}

        {/* EMPATE */}
        {atual.status === 'EMPATE' && (
          <>
            <div style={{ background: 'rgba(245,158,11,.15)', border: '0.3vh solid #f59e0b', borderRadius: '1.6vh', padding: '1.8vh', textAlign: 'center', fontSize: '2.8vh', fontWeight: 900, color: '#fde68a', letterSpacing: 1 }}>
              ⚖️ EMPATE ENTRE AS CHAPAS {atual.chapas.filter(c => atual.empatadas.includes(c.id)).map(c => c.numero).join(' E ')} — AGUARDANDO DEFINIÇÃO DA MESA, CONFORME O REGIMENTO INTERNO
            </div>
            <div style={{ flex: 1, display: 'grid', gridTemplateColumns: `repeat(${Math.min(atual.chapas.length, 3)}, 1fr)`, gap: '2.2vh', minHeight: 0 }}>
              {atual.chapas.map(c => {
                const emp = atual.empatadas.includes(c.id);
                return (
                  <div key={c.id} style={{ borderRadius: '1.8vh', border: `0.4vh solid ${emp ? '#f59e0b' : 'rgba(255,255,255,.12)'}`, background: emp ? 'rgba(245,158,11,.1)' : 'rgba(255,255,255,.04)', padding: '2.2vh', display: 'flex', flexDirection: 'column', gap: '1.2vh', opacity: emp ? 1 : 0.45, minHeight: 0 }}>
                    {emp && <span style={{ alignSelf: 'flex-start', background: '#f59e0b', color: '#451a03', fontWeight: 900, letterSpacing: 2, fontSize: '1.7vh', padding: '0.6vh 1.6vh', borderRadius: 999 }}>EMPATE</span>}
                    <div style={{ fontSize: '3vh', fontWeight: 800 }}>{rotuloChapa(c)}</div>
                    <div style={{ display: 'flex', gap: '1vh' }}>
                      {c.membros.map(m => (
                        <div key={m.cargo} title={`${m.cargo}: ${m.nome}`} style={{ width: '7vh', height: '9vh', borderRadius: '0.8vh', overflow: 'hidden', display: 'flex', border: '0.2vh solid rgba(255,255,255,.15)' }}><Foto url={fotoDe(m.vereadorId)} nome={m.nome} /></div>
                      ))}
                    </div>
                    <div style={{ fontSize: '1.7vh', color: '#cbd5e1', lineHeight: 1.6 }}>{c.membros.map(m => <div key={m.cargo}>{m.cargo}: <b style={{ color: '#fff' }}>{m.nome}</b></div>)}</div>
                    <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', borderTop: '1px solid rgba(255,255,255,.12)', paddingTop: '1.2vh' }}>
                      <span style={{ fontSize: '1.8vh', color: '#94a3b8', fontWeight: 700, letterSpacing: 2 }}>VOTOS</span>
                      <b style={{ fontFamily: 'monospace', fontSize: '7vh', fontWeight: 900, lineHeight: 1 }}>{pad(c.votos)}</b>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '3vh', fontSize: '2.2vh', color: '#cbd5e1' }}>
              <span>Abstenções: <b style={{ color: '#fff' }}>{pad(atual.abstencoes)}</b></span>
              <span>Votaram: <b style={{ color: '#fff' }}>{atual.votaram} de {atual.totalVereadores}</b></span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
